import React, { memo, ReactNode, useCallback, useEffect } from 'react';
import { DOMProps } from '@react-types/shared';
import { usePreviousRef, useResizeObserver } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import './BaseSliderAnimation.css';
import { animateEaseOutQuadFast } from '../../lib/animate/animate';
import { useHStack } from '../../hooks/useStack';
import { StackGap } from '../Stack';

const cls = cn('BaseSliderAnimation');

export interface BaseSliderAnimationProps extends TestProps, DOMProps {
    /**
     * Массив нод которые нужно листать
     */
    items: ReactNode[];

    /**
     * Номер выбранного в данные момент элемента
     */
    selectedItemIndex: number;

    /**
     * Длительность анимации свайпа
     * @default 300
     */
    animationDuration?: number;

    /**
     * Названия классов для слайдера
     */
    className?: string;

    /**
     * Названия классов для элемента слайдера
     */
    itemClassName?: string;

    /**
     * Расстояние между элементами
     */
    gap?: StackGap;
}

type TrancatedRect = {
    width: number;
    height: number;
};

/**
 * Компонент принимает на вход массив элементов ReactNode и анимирует их листание слева направо и справа налево
 */
export const BaseSliderAnimation = memo<BaseSliderAnimationProps>(function BaseSliderAnimation(props) {
    const { LogProvider } = useLogger('MobileSwiper');
    const {
        items,
        className,
        itemClassName,
        selectedItemIndex,
        animationDuration = 300,
        gap = '0',
        'data-testid': dataTestId = 'BaseSliderAnimation',
    } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const previousSelectedIndex = usePreviousRef<number>(selectedItemIndex).current;

    const containerContentRectRef = React.useRef<TrancatedRect>({
        width: 0,
        height: 0,
    });

    const translateRef = React.useRef(0);
    const minTranslateValueRef = React.useRef(0);

    const animateTranslate = useCallback(
        (start: number, diff: number) => {
            return animateEaseOutQuadFast({
                duration: animationDuration,
                step(t: number) {
                    translateRef.current = start + diff * t;
                    if (listRef.current) {
                        listRef.current.style.transform = `translateX(${translateRef.current}px`;
                    }
                },
            });
        },
        [animationDuration],
    );

    const getContentSize = useCallback(() => {
        const width = containerContentRectRef.current?.width;

        return width || 0;
    }, []);

    const animateSelectedIndexChange = React.useCallback(async () => {
        const width = getContentSize();
        const widthDiff = -translateRef.current - selectedItemIndex * (width + Number(gap));

        await animateTranslate(translateRef.current, widthDiff);
    }, [animateTranslate, gap, getContentSize, selectedItemIndex]);

    useEffect(() => {
        if (previousSelectedIndex !== undefined && selectedItemIndex !== previousSelectedIndex) {
            animateSelectedIndexChange().catch(console.error);
        }
    }, [selectedItemIndex, previousSelectedIndex, animateSelectedIndexChange]);

    const onResize = useCallback(
        (entries: ResizeObserverEntry[]) => {
            for (const entry of entries) {
                switch (true) {
                    case entry.target === containerRef.current: {
                        const { contentRect } = entry;
                        containerContentRectRef.current = contentRect;
                        // хотя selectedIndex не изменился, нам нужно перевыполнить рассчеты для анимаций
                        animateSelectedIndexChange().catch(console.error);
                        break;
                    }
                    case entry.target === listRef.current: {
                        const { scrollWidth = 0, offsetWidth = 0 } = listRef.current || {};
                        minTranslateValueRef.current = -(scrollWidth - offsetWidth);
                        // хотя selectedIndex не изменился, нам нужно перевыполнить рассчеты для анимаций
                        animateSelectedIndexChange().catch(console.error);
                        break;
                    }
                }
            }
        },
        [animateSelectedIndexChange],
    );
    const stackClass = useHStack({ gap });
    const resizeObserver = useResizeObserver(onResize);

    useEffect(() => {
        if (resizeObserver) {
            containerRef.current && resizeObserver.observe(containerRef.current);
            listRef.current && resizeObserver.observe(listRef.current);
        }
    }, [resizeObserver]);

    return (
        <LogProvider>
            <div ref={containerRef} className={cls('list-container', [className])} data-testid={dataTestId}>
                <div className={cls('list', [stackClass])} ref={listRef}>
                    {items.map((item, index) => {
                        const selected = index === selectedItemIndex;
                        const itemDataTestId = `${dataTestId}.Item${index}${selected ? '.Selected' : ''}`;
                        return (
                            <div
                                data-testid={itemDataTestId}
                                className={cls('item', { selected }, [itemClassName])}
                                key={`item-${index}`}
                            >
                                {item}
                            </div>
                        );
                    })}
                </div>
            </div>
        </LogProvider>
    );
});
